import { React, useEffect, useState, useRef } from 'react';

import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { Alerta } from '../../components/alerta';
import PageHeader from '../../components/pageHeader';
import { fetchFlask } from '../../utils/fetchFlask';
import { maskCpfCnpj } from '../../utils/maskCpfCnpj';
import { formatCurrency } from '../../utils/stringFormat';

const ValidarSerasa = () => {
    const [cadastro, setCadastro] = useState('');
    const [listaCadastros, setListaCadastros] = useState([]);
    const [aprovado, setAprovado] = useState(false);
    const [relatorio, setRelatorio] = useState('');
    const [message, setMessage] = useState('');
    const [status, setStatus] = useState('');
    const [openAlerta, setOpenAlerta] = useState(false);
    const [processando, setProcessando] = useState('');

    const hasFetched = useRef(false);

    async function buscarCadastrosAmarelos() {
        const response = await fetchFlask('buscarSerasaAmarelo');
        if (response.status !== 'error') {
            setListaCadastros(response.doc_list);
        } else {
            setMessage(response.message);
            setStatus(response.status);
            setOpenAlerta(true);
        }
    }

    useEffect(() => {
        if (hasFetched.current) return;
        hasFetched.current = true;
        buscarCadastrosAmarelos();
    }, []);

    useEffect(() => {
        if (cadastro) {
            getSerasaReport();
        }
    }, [cadastro]);

    async function getSerasaReport() {
        const body = JSON.stringify({
            document: cadastro
        });

        const response = await fetchFlask('buscarRelatorioSerasaAgent', body);

        if (response.status === 'success') {
            setRelatorio(response.content);
        } else {
            setMessage(response.message);
            setStatus(response.status);
            setOpenAlerta(true);
        }
    }

    async function submitApproveSerasa(e) {
        e.preventDefault();

        const body = JSON.stringify({
            approved: aprovado,
            document: cadastro
        });

        setProcessando(aprovado ? 'aprovar' : 'reprovar');
        try {
            const response = await fetchFlask('chatgpt/validarSerasa', body);
            setMessage(response.message);
            setStatus(response.status);
            setOpenAlerta(true);

            if (response.status === 'success') {
                setCadastro('');
                await buscarCadastrosAmarelos();
                setRelatorio('');
                setAprovado(false);
            }
        } finally {
            setProcessando('');
        }
    }

    async function submitRecalculateSerasa(e) {
        e.preventDefault();

        const body = JSON.stringify({
            document: cadastro
        });

        setProcessando('recalcular');
        try {
            let response = await fetchFlask('deletarDocumentoSerasa', body);
            if (response.status === 'error') {
                setMessage(response.message);
                setOpenAlerta(true);
            }

            response = await fetchFlask('checkSerasa', body);
            setMessage(response.message);
            setStatus(response.status);
            setOpenAlerta(true);

            if (response.status === 'success') {
                setCadastro('');
                await buscarCadastrosAmarelos();
                setRelatorio('');
            }
        } finally {
            setProcessando('');
        }
    }

    const mensagensProcessando = {
        aprovar: 'Aprovando cadastro e processando a criação do contrato...',
        reprovar: 'Registrando a reprovação do cadastro...',
        recalcular: 'Recalculando o Serasa...'
    };

    const spinnerBotao = (acao) => processando === acao && <CircularProgress size={20} color="inherit" />;

    return (
        <Box className="mainBox">
            <Alerta
                texto={message}
                status={status}
                open={openAlerta}
                handleClose={() => setOpenAlerta(false)}
            />

            <Grid container alignItems="center" justifyContent="center" marginBottom={2} spacing={2}>
                <Grid item xs={12}>
                    <PageHeader>Aprovar Serasa</PageHeader>
                </Grid>

                <Grid item xs={8}>
                    <form action="" method="POST" id="formSerasaApproval" onSubmit={submitApproveSerasa}>
                        <Grid item xs={12}>
                            <Box display="flex" justifyContent="center" mt={2}>
                                <FormControl sx={{ display: 'flex' }}>
                                    <Autocomplete
                                        sx={{ width: '300px' }}
                                        value={cadastro}
                                        options={listaCadastros}
                                        disabled={!!processando}
                                        getOptionLabel={(option) => maskCpfCnpj(option)}
                                        onChange={(e, value) => setCadastro(value)}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Cadastro Serasa"
                                                required
                                                value={maskCpfCnpj(cadastro)}
                                            />
                                        )}
                                    />
                                </FormControl>
                            </Box>
                        </Grid>

                        {relatorio && (
                            <>
                                <Grid item xs={12}>
                                    <TableContainer component={Paper} sx={{ margin: 'auto', mt: 4 }}>
                                        <Typography variant="h6" sx={{ p: 2 }}>
                                            Relatório Serasa
                                        </Typography>
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell><strong>Credor</strong></TableCell>
                                                    <TableCell><strong>UF</strong></TableCell>
                                                    <TableCell><strong>Natureza</strong></TableCell>
                                                    <TableCell><strong>Dívida (R$)</strong></TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {relatorio.flat().map((row, idx) => (
                                                    <TableRow key={idx}>
                                                        <TableCell>{row.creditorName}</TableCell>
                                                        <TableCell>{row.federalUnit}</TableCell>
                                                        <TableCell>{row.legalNature}</TableCell>
                                                        <TableCell>{formatCurrency(row.amount)}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Grid>

                                <Grid container justifyContent="center" spacing={2} marginTop={2}>
                                    <Grid item>
                                        <Button
                                            onClick={() => setAprovado(true)}
                                            type="submit"
                                            variant="contained"
                                            disabled={!!processando}
                                            endIcon={spinnerBotao('aprovar')}
                                            sx={{
                                                backgroundColor: 'green',
                                                color: 'white',
                                                '&:hover': { backgroundColor: 'darkgreen' }
                                            }}
                                        >
                                            Aprovar Cadastro
                                        </Button>
                                    </Grid>
                                    <Grid item>
                                        <Button
                                            onClick={() => setAprovado(false)}
                                            type="submit"
                                            variant="contained"
                                            disabled={!!processando}
                                            endIcon={spinnerBotao('reprovar')}
                                            sx={{
                                                backgroundColor: 'red',
                                                color: 'white',
                                                '&:hover': { backgroundColor: 'darkred' }
                                            }}
                                        >
                                            Reprovar Cadastro
                                        </Button>
                                    </Grid>
                                </Grid>
                            </>
                        )}
                    </form>
                    <form action="" method="POST" id="formRecalculateSerasa" onSubmit={submitRecalculateSerasa}>
                        {relatorio && (
                            <>
                                <Grid container justifyContent="center" spacing={2} marginTop={2}>
                                    <Grid item>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            disabled={!!processando}
                                            endIcon={spinnerBotao('recalcular')}
                                            sx={{
                                                backgroundColor: '#FFA300',
                                                color: '#13154F',
                                                '&:hover': { backgroundColor: '#CC8400' }
                                            }}
                                        >
                                            Recalcular serasa
                                        </Button>
                                    </Grid>
                                </Grid>
                            </>
                        )}
                    </form>
                    {processando && (
                        <Box display="flex" alignItems="center" justifyContent="center" gap={2} mt={3}>
                            <CircularProgress size={28} />
                            <Typography>{mensagensProcessando[processando]}</Typography>
                        </Box>
                    )}
                </Grid>
            </Grid>
        </Box>
    );
};

export default ValidarSerasa;
